import React from 'react'
import { graphql, Link } from 'gatsby'
import get from 'lodash/get'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import SiteMeta from '../components/meta-data'
import Footer from '../components/footer'

const academyStats = [
    { value: '5-Star', label: 'Rated by professionals' },
    { value: '100%', label: 'Immediately applicable' },
    { value: 'B2C & B2B', label: 'Sectors covered' },
    { value: '15+', label: 'Years China experience' },
]

const academyModules = [
    {
        title: 'Chinese Social Media Strategy',
        description: 'WeChat, Xiaohongshu, Douyin and Weibo each play by different rules. This module covers how each platform works, what content performs, and how to build a plan that fits your brand and budget.',
        tags: ['WeChat', 'XHS', 'Douyin', 'Weibo'],
    },
    {
        title: 'KOL & Influencer Marketing in China',
        description: 'KOLs are not interchangeable. This module covers how to find the right ones for your category, what to put in a brief, how to negotiate fees, and what to actually measure once the campaign ends.',
        tags: ['KOL', 'KOC', 'ROI'],
    },
    {
        title: 'China E-Commerce & Social Commerce',
        description: 'Tmall, JD.com, Douyin Shop and Xiaohongshu all work differently. This module covers store setup, product pages that convert, and how cross-border entry actually works for brands outside China.',
        tags: ['Tmall', 'JD.com', 'Social Commerce'],
    },
    {
        title: 'Chinese Content Marketing & Advertising',
        description: 'What works on Instagram will not land on Xiaohongshu. This module covers how Chinese audiences read content, what visuals they respond to, how paid ads work on WeChat and Douyin, and how to read your numbers properly.',
        tags: ['Content', 'Paid Media', 'Creative'],
    },
    {
        title: 'Chinese Live-Streaming & Short Video',
        description: 'Live-selling in China is not a gimmick. Brands are running daily streams and converting viewers in real time. This module covers how it works technically, what a good stream looks like, and where short video fits in your overall plan.',
        tags: ['Live Commerce', 'Douyin', 'Short Video'],
    },
    {
        title: 'China AI Marketing: DeepSeek, Doubao & Kimi',
        description: 'Most Western marketers have never heard of these tools. This module gives you hands-on time with DeepSeek, Doubao and Kimi so you can produce Chinese content for Xiaohongshu, WeChat and Douyin without needing a dedicated native team.',
        tags: ['DeepSeek', 'Doubao', 'Kimi', 'AI Content'],
        isNew: true,
    },
]

const academyTestimonials = [
    {
        course: 'Chinese Social Media Training',
        quote: "A genuine game-changer. It not only gave me actionable strategies but also sparked new ideas I am now implementing in my company's strategy.",
        role: 'Global Social Media & Influencer Marketing Manager',
        org: 'Luxury & Anti-Aging Skincare Brand',
    },
    {
        course: 'Chinese Social Media Training',
        quote: 'It has not just expanded my knowledge, it has given me the confidence to make more informed, strategic decisions.',
        role: 'Partner, Head of Digital & Creative',
        org: 'Leading UK Property & Rural Consultancy',
    },
    {
        course: 'China E-Commerce Course',
        quote: 'The training provided a clear and practical understanding of the Chinese e-commerce landscape. I would highly recommend this to professionals wanting to strengthen their knowledge.',
        role: 'Senior Business Development Manager',
        org: 'China E-Commerce Platform',
    },
    {
        course: 'Chinese Social Media Training',
        quote: 'This training deepened my understanding of the ecosystem and enabled me to craft more purposeful, forward-thinking content strategies.',
        role: 'Senior Social Media & Content Executive',
        org: 'International Student Property Group',
    },
    {
        course: 'Digital Marketing Training for China',
        quote: 'This helped me kick-start our social media presence in China with renewed energy and ideas. A fantastic trainer. I gained a lot of hands-on experience.',
        role: 'Digital Marketer',
        org: 'Global IT & Software Consultancy',
    },
    {
        course: 'Chinese Digital Marketing Training',
        quote: 'Extremely practical, well-structured, and supported by rich case studies. The methods and tools can be directly applied to daily work, a highly valuable and rewarding learning experience.',
        role: 'Sales & Marketing Professional',
        org: 'Global Steel Manufacturer, China Division',
    },
]

class AcademyPage extends React.Component {
    render() {
        const metaData = get(this.props, 'data.site.siteMetadata.academyPage')

        return (
            <div className="academy-page">
                <SiteMeta metaData={metaData} />
                <AcademyPageStyles />
                <HeroSection />
                <StatsSection />
                <IdentityStrip />
                <ModuleSection />
                <TestimonialSection />
                <TrainerSection />
                <FormSection />
                <Footer />
            </div>
        )
    }
}

const AcademyPageStyles = () => (
    <style>{`
        .academy-page {
            --academy-red: #C8102E;
            --academy-red-dark: #a00e24;
            --academy-dark: #1a1a1a;
            --academy-mid: #444;
            --academy-light: #f5f5f5;
            --academy-border: #e0e0e0;
            --academy-gold: #f0c040;
            background: #fff;
            color: #222;
        }

        .academy-page .academy-nav {
            position: sticky;
            top: 0;
            z-index: 30;
            background: rgba(255, 255, 255, 0.98);
            border-bottom: 2px solid var(--academy-red);
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }

        .academy-page .academy-nav-inner,
        .academy-page .academy-section-inner,
        .academy-page .academy-form-intro {
            width: min(1100px, calc(100% - 3rem));
            margin: 0 auto;
        }

        .academy-page .academy-nav-inner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 0.9rem 0;
        }

        .academy-page .academy-logo {
            display: inline-flex;
            align-items: center;
            gap: 0.85rem;
            color: var(--academy-dark);
            font-weight: 800;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            font-size: 0.95rem;
        }

        .academy-page .academy-logo img {
            width: 52px;
            height: 52px;
            object-fit: cover;
            border-radius: 4px;
        }

        .academy-page .academy-logo span {
            color: var(--academy-red);
        }

        .academy-page .academy-nav-cta,
        .academy-page .academy-hero-primary {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.95rem 1.8rem;
            border-radius: 3px;
            background: var(--academy-red);
            color: #fff;
            font-size: 0.9rem;
            font-weight: 800;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            transition: background 0.2s ease, transform 0.15s ease;
        }

        .academy-page .academy-nav-cta:hover,
        .academy-page .academy-hero-primary:hover {
            background: var(--academy-red-dark);
            color: #fff;
            transform: translateY(-2px);
        }

        .academy-page .academy-hero-secondary {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.85rem 1.7rem;
            border-radius: 3px;
            border: 2px solid rgba(255, 255, 255, 0.5);
            color: #fff;
            font-size: 0.9rem;
            font-weight: 700;
            transition: border-color 0.2s ease, transform 0.15s ease;
        }

        .academy-page .academy-hero-secondary:hover {
            border-color: #fff;
            color: #fff;
            transform: translateY(-2px);
        }

        .academy-page .academy-hero {
            background: linear-gradient(135deg, #1a1a1a 0%, #2d0a12 60%, #c8102e 100%);
            color: #fff;
            padding: 5.5rem 0 5rem;
            text-align: center;
        }

        .academy-page .academy-hero-inner {
            width: min(1100px, calc(100% - 3rem));
            margin: 0 auto;
        }

        .academy-page .academy-eyebrow,
        .academy-page .academy-section-tag,
        .academy-page .academy-trainer-tag {
            display: inline-block;
            padding: 0.35rem 0.85rem;
            border-radius: 2px;
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
        }

        .academy-page .academy-eyebrow,
        .academy-page .academy-trainer-tag {
            background: var(--academy-red);
            color: #fff;
        }

        .academy-page .academy-section-tag {
            background: #fce9ec;
            color: var(--academy-red);
            margin-bottom: 1rem;
        }

        .academy-page .academy-eyebrow {
            margin-bottom: 1.4rem;
        }

        .academy-page .academy-hero h1 {
            max-width: 780px;
            margin: 0 auto 2rem;
            color: #fff;
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 900;
            line-height: 1.18;
        }

        .academy-page .academy-hero h1 em,
        .academy-page .academy-section-title em {
            color: var(--academy-red);
            font-style: normal;
        }

        .academy-page .academy-hero-line2 {
            display: block;
            margin-top: 0.9rem;
            color: rgba(255, 255, 255, 0.84);
            font-size: clamp(0.95rem, 1.9vw, 1.35rem);
            font-weight: 600;
            line-height: 1.4;
        }

        .academy-page .academy-hero-actions {
            display: flex;
            justify-content: center;
            gap: 0.9rem;
            flex-wrap: wrap;
        }

        .academy-page .academy-stats {
            background: var(--academy-red);
            color: #fff;
        }

        .academy-page .academy-stats-inner {
            width: min(1200px, 100%);
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .academy-page .academy-stat {
            padding: 1.75rem 1.25rem;
            text-align: center;
            border-right: 1px solid rgba(255, 255, 255, 0.25);
        }

        .academy-page .academy-stat:last-child {
            border-right: 0;
        }

        .academy-page .academy-stat-value {
            font-size: 2rem;
            font-weight: 900;
            line-height: 1;
        }

        .academy-page .academy-stat-label {
            margin-top: 0.35rem;
            font-size: 0.8rem;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            opacity: 0.86;
        }

        .academy-page .academy-identity {
            padding: 3rem 0;
            border-top: 1px solid var(--academy-border);
            border-bottom: 1px solid var(--academy-border);
            text-align: center;
        }

        .academy-page .academy-identity p {
            max-width: 740px;
            margin: 0 auto;
            font-size: clamp(1.05rem, 1.8vw, 1.25rem);
            line-height: 1.7;
            font-weight: 500;
        }

        .academy-page .academy-identity strong {
            color: var(--academy-red);
        }

        .academy-page .academy-identity-sub {
            margin-top: 0.9rem;
            color: #777;
            font-size: 0.92rem;
            font-weight: 400;
        }

        .academy-page .academy-section,
        .academy-page .request-form {
            padding: 5rem 0;
        }

        .academy-page .academy-section-title {
            margin: 0 0 0.9rem;
            color: var(--academy-dark);
            font-size: clamp(1.8rem, 3vw, 2.45rem);
            font-weight: 800;
            line-height: 1.2;
        }

        .academy-page .academy-section-lead {
            max-width: 680px;
            margin-bottom: 3rem;
            color: var(--academy-mid);
            font-size: 1.05rem;
            line-height: 1.7;
        }

        .academy-page .academy-modules {
            background: var(--academy-light);
        }

        .academy-page .academy-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
            gap: 1.5rem;
        }

        .academy-page .academy-module-card,
        .academy-page .academy-testimonial-card {
            background: #fff;
            border: 1px solid var(--academy-border);
        }

        .academy-page .academy-module-card {
            position: relative;
            padding: 1.9rem 1.7rem;
            border-radius: 4px;
            transition: box-shadow 0.2s ease, transform 0.2s ease;
        }

        .academy-page .academy-module-card:hover,
        .academy-page .academy-testimonial-card:hover {
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
            transform: translateY(-3px);
        }

        .academy-page .academy-module-card.is-new::before {
            content: "New";
            position: absolute;
            top: 0.9rem;
            right: 0.9rem;
            padding: 0.25rem 0.55rem;
            border-radius: 2px;
            background: var(--academy-red);
            color: #fff;
            font-size: 0.65rem;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
        }

        .academy-page .academy-module-num,
        .academy-page .academy-testimonial-course {
            margin-bottom: 0.65rem;
            color: var(--academy-red);
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
        }

        .academy-page .academy-module-card h3,
        .academy-page .academy-testimonial-role {
            color: var(--academy-dark);
        }

        .academy-page .academy-module-card h3 {
            margin-bottom: 0.65rem;
            font-size: 1.08rem;
            font-weight: 800;
            line-height: 1.35;
        }

        .academy-page .academy-module-card p {
            color: var(--academy-mid);
            font-size: 0.92rem;
            line-height: 1.65;
        }

        .academy-page .academy-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 0.45rem;
            margin-top: 1rem;
        }

        .academy-page .academy-tag {
            padding: 0.2rem 0.6rem;
            border-radius: 2px;
            background: #fce9ec;
            color: var(--academy-red);
            font-size: 0.7rem;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
        }

        .academy-page .academy-tag.is-dark {
            background: #e8e8e8;
            color: #444;
        }

        .academy-page .academy-testimonials {
            background: #fff;
        }

        .academy-page .academy-testimonial-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.6rem;
        }

        .academy-page .academy-testimonial-card {
            padding: 1.9rem 1.75rem;
            border-left: 5px solid var(--academy-red);
            border-radius: 0 4px 4px 0;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            transition: box-shadow 0.2s ease, transform 0.2s ease;
        }

        .academy-page .academy-stars {
            color: var(--academy-gold);
            font-size: 1rem;
            letter-spacing: 2px;
        }

        .academy-page .academy-testimonial-quote {
            color: #333;
            font-size: 0.97rem;
            font-style: italic;
            line-height: 1.7;
        }

        .academy-page .academy-testimonial-divider {
            margin: 0;
            border: 0;
            border-top: 1px solid var(--academy-border);
        }

        .academy-page .academy-testimonial-role {
            font-size: 0.9rem;
            font-weight: 700;
        }

        .academy-page .academy-testimonial-org {
            margin-top: 0.2rem;
            color: #777;
            font-size: 0.84rem;
        }

        .academy-page .academy-trainer {
            background: var(--academy-dark);
            color: #fff;
        }

        .academy-page .academy-trainer-wrap {
            display: grid;
            grid-template-columns: 260px 1fr;
            gap: 3.75rem;
            align-items: start;
        }

        .academy-page .academy-trainer-avatar {
            width: 260px;
            height: 300px;
            overflow: hidden;
            border: 3px solid var(--academy-red);
            border-radius: 4px;
        }

        .academy-page .academy-trainer-avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center top;
        }

        .academy-page .academy-trainer-name {
            margin: 0 0 0.45rem;
            color: #fff;
            font-size: 1.95rem;
            font-weight: 900;
        }

        .academy-page .academy-trainer-title {
            margin-bottom: 1.4rem;
            color: rgba(255, 255, 255, 0.65);
            font-size: 0.95rem;
        }

        .academy-page .academy-trainer-bio {
            margin-bottom: 1.2rem;
            color: rgba(255, 255, 255, 0.82);
            font-size: 0.96rem;
            line-height: 1.75;
        }

        .academy-page .academy-trainer-expertise-title {
            margin-bottom: 0.75rem;
            color: var(--academy-red);
            font-size: 0.8rem;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
        }

        .academy-page .academy-expertise-list {
            margin: 0;
            padding: 0;
            list-style: none;
        }

        .academy-page .academy-expertise-list li {
            margin-bottom: 0.6rem;
            color: rgba(255, 255, 255, 0.86);
            font-size: 0.9rem;
            line-height: 1.55;
        }

        .academy-page .academy-expertise-list li::before {
            content: "- ";
            color: var(--academy-red);
            font-weight: 700;
        }

        .academy-page .academy-credentials {
            display: flex;
            flex-wrap: wrap;
            gap: 0.65rem;
            margin-top: 1.4rem;
        }

        .academy-page .academy-credential {
            padding: 0.35rem 0.85rem;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.92);
            font-size: 0.78rem;
            font-weight: 600;
        }

        .academy-page .request-form {
            background: var(--academy-light);
        }

        .academy-page .academy-form-left {
            width: 100%;
        }

        .academy-page .academy-form-left .academy-section-tag {
            margin-bottom: 1rem;
        }

        .academy-page .academy-form-left h2 {
            margin: 0 0 16px;
            color: var(--academy-dark);
            font-size: clamp(1.6rem, 2.8vw, 2.1rem);
            font-weight: 800;
            line-height: 1.2;
        }

        .academy-page .academy-form-left h2 em {
            color: var(--academy-red);
            font-style: normal;
        }

        .academy-page .academy-form-left p {
            margin-bottom: 32px;
            color: var(--academy-mid);
            font-size: 0.95rem;
            line-height: 1.7;
        }

        .academy-page .academy-form-reassure {
            margin: 0;
            padding: 0;
            list-style: none;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .academy-page .academy-form-reassure li {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            color: var(--academy-mid);
            font-size: 0.9rem;
            line-height: 1.6;
        }

        .academy-page .academy-form-reassure strong {
            color: var(--academy-dark);
        }

        .academy-page .academy-form-reassure .icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            flex-shrink: 0;
            border-radius: 50%;
            background: var(--academy-red);
            color: #fff;
            font-size: 0.75rem;
            margin-top: 1px;
        }

        .academy-page .request-form {
            padding: 80px 5%;
        }

        .academy-page .request-form .container {
            max-width: 1100px;
            width: 100%;
        }

        .academy-page .request-form .columns {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 70px;
            align-items: start;
            margin: 0;
        }

        .academy-page .request-form .column.is-6 {
            display: block;
            width: auto;
            padding: 0;
        }

        .academy-page .request-form .column.is-6:last-child {
            background: transparent;
            border: 0;
            border-radius: 0;
            padding: 0;
            box-shadow: none;
            min-height: auto;
        }

        .academy-page .request-form .academy-form-card {
            width: 100%;
            background: #fff;
            border: 1px solid var(--academy-border);
            border-top: 4px solid var(--academy-red);
            border-radius: 4px;
            padding: 40px 36px;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
        }

        .academy-page .request-form .title,
        .academy-page .request-form .label {
            color: var(--academy-dark);
        }

        .academy-page .request-form .academy-form-card-title {
            margin-bottom: 6px;
            color: var(--academy-dark);
            font-size: 1.15rem;
            font-weight: 800;
        }

        .academy-page .request-form .academy-form-card-subtitle {
            margin-bottom: 28px;
            color: #777;
            font-size: 0.85rem;
        }

        .academy-page .request-form .academy-form-inner {
            width: 100%;
        }

        .academy-page .request-form .field {
            margin-bottom: 18px;
        }

        .academy-page .request-form .academy-form-status {
            margin-bottom: 1rem;
            color: var(--academy-red);
            font-size: 0.92rem;
            line-height: 1.6;
        }

        .academy-page .request-form .input {
            width: 100%;
            padding: 11px 14px;
            border: 1px solid #d0d0d0;
            border-radius: 3px;
            background: #fafafa;
            color: var(--academy-dark);
            font-family: inherit;
            font-size: 0.9rem;
            box-shadow: none;
            transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .academy-page .request-form .input:focus {
            background: #fff;
            border-color: var(--academy-red);
            box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.1);
        }

        .academy-page .request-form .control.has-icons-left .icon {
            display: none;
        }

        .academy-page .request-form .control.has-icons-left .input {
            padding-left: 11px;
        }

        .academy-page .request-form .label {
            display: block;
            margin-bottom: 6px;
            font-size: 0.78rem;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
        }

        .academy-page .request-form .button.is-danger {
            background: var(--academy-red);
            border: none;
            border-radius: 3px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            font-size: 0.95rem;
            font-weight: 800;
            letter-spacing: 0.06em;
            text-transform: uppercase;
        }

        .academy-page .request-form .button.is-danger:hover {
            background: var(--academy-red-dark);
            transform: translateY(-2px);
        }

        .academy-page .request-form .control.has-text-centered {
            text-align: left !important;
        }

        .academy-page .request-form .button.is-danger.is-medium {
            width: 100%;
            padding: 15px;
            height: auto;
            margin-top: 20px;
        }

        @media (max-width: 900px) {
            .academy-page .academy-stats-inner {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .academy-page .academy-stat:nth-child(2) {
                border-right: 0;
            }

            .academy-page .academy-stat:nth-child(-n + 2) {
                border-bottom: 1px solid rgba(255, 255, 255, 0.25);
            }

            .academy-page .academy-trainer-wrap {
                grid-template-columns: 1fr;
                gap: 2rem;
            }

            .academy-page .request-form .columns {
                grid-template-columns: 1fr;
                gap: 40px;
            }
        }

        @media (max-width: 780px) {
            .academy-page .academy-nav-inner,
            .academy-page .academy-section-inner,
            .academy-page .academy-hero-inner,
            .academy-page .academy-form-intro {
                width: min(1100px, calc(100% - 1.5rem));
            }

            .academy-page .academy-nav-inner {
                padding: 0.75rem 0;
            }

            .academy-page .academy-logo {
                font-size: 0.8rem;
                letter-spacing: 0.04em;
            }

            .academy-page .academy-logo img {
                width: 42px;
                height: 42px;
            }

            .academy-page .academy-nav-cta {
                padding: 0.75rem 1rem;
                font-size: 0.75rem;
            }

            .academy-page .academy-stats-inner {
                grid-template-columns: 1fr;
            }

            .academy-page .academy-stat {
                border-right: 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.25);
            }

            .academy-page .academy-stat:last-child {
                border-bottom: 0;
            }

            .academy-page .academy-section,
            .academy-page .request-form {
                padding: 4rem 0;
            }

            .academy-page .academy-hero {
                padding: 4rem 0 3.5rem;
            }

            .academy-page .academy-hero-line2 {
                white-space: normal;
            }

            .academy-page .request-form .column.is-6:last-child {
                padding: 0;
            }

            .academy-page .request-form .columns {
                margin-left: 0;
                margin-right: 0;
            }

            .academy-page .academy-form-left {
                padding: 0;
            }

            .academy-page .request-form .academy-form-card {
                padding: 40px 36px;
            }
        }
    `}</style>
)

class HeroSection extends React.Component {
    render() {
        return (
            <>
                <nav className="academy-nav">
                    <div className="academy-nav-inner">
                        <Link to="/academy" className="academy-logo">
                            <img src="/img/academy-logo.jpg" alt="Red Digital Academy logo" />
                            <div>Red Digital <span>Academy</span></div>
                        </Link>
                        <a href="#form" className="academy-nav-cta">Enquire Now</a>
                    </div>
                </nav>
                <section className="academy-hero">
                    <div className="academy-hero-inner">
                        <div className="academy-eyebrow">China Digital Marketing Training</div>
                        <h1>
                            China Digital Marketing Is <em>Changing Fast.</em>
                            <span className="academy-hero-line2">Expert-led training trusted by senior marketers at leading brands</span>
                        </h1>
                        <div className="academy-hero-actions">
                            <a href="#form" className="academy-hero-primary">Enquire About Training</a>
                            <a href="#testimonials" className="academy-hero-secondary">Read Client Results</a>
                        </div>
                    </div>
                </section>
            </>
        )
    }
}

const StatsSection = () => (
    <div className="academy-stats">
        <div className="academy-stats-inner">
            {academyStats.map((stat, index) => (
                <div className="academy-stat" key={index}>
                    <div className="academy-stat-value">{stat.value}</div>
                    <div className="academy-stat-label">{stat.label}</div>
                </div>
            ))}
        </div>
    </div>
)

const IdentityStrip = () => (
    <div className="academy-identity">
        <div className="academy-section-inner">
            <p>
                China rewards the marketers who understand it properly. <strong>This training exists for professionals who want to be that person in their organisation.</strong>
            </p>
            <p className="academy-identity-sub">
                You will leave able to build a credible China strategy, brief any agency with authority, and spot weak thinking before it wastes your budget.
            </p>
        </div>
    </div>
)

class ModuleSection extends React.Component {
    render() {
        return (
            <section className="academy-section academy-modules">
                <div className="academy-section-inner">
                    <div className="academy-section-tag">Training Modules</div>
                    <h2 className="academy-section-title">
                        What You Will Learn.<br />
                        <em>Every Platform That Matters in China.</em>
                    </h2>
                    <p className="academy-section-lead">
                        Six focused modules. When you finish, you will not need to rely on an agency to tell you what is working, or what is not.
                    </p>
                    <div className="academy-grid">
                        {academyModules.map((module, index) => (
                            <article className={`academy-module-card${module.isNew ? ' is-new' : ''}`} key={index}>
                                <div className="academy-module-num">{`Module 0${index + 1}`}</div>
                                <h3>{module.title}</h3>
                                <p>{module.description}</p>
                                <div className="academy-tags">
                                    {module.tags.map((tag, tagIndex) => (
                                        <span className={`academy-tag${tag === 'AI Content' ? ' is-dark' : ''}`} key={tagIndex}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        )
    }
}

const TestimonialSection = () => (
    <section id="testimonials" className="academy-section academy-testimonials">
        <div className="academy-section-inner">
            <div className="academy-section-tag">Client Results</div>
            <h2 className="academy-section-title">In Their Own Words</h2>
            <p className="academy-section-lead">
                Senior professionals across consumer brands, property, technology and manufacturing all walked in with the same problem, and left with the clarity to act on it.
            </p>
            <div className="academy-testimonial-grid">
                {academyTestimonials.map((item, index) => (
                    <article className="academy-testimonial-card" key={index}>
                        <div className="academy-testimonial-course">{item.course}</div>
                        <div className="academy-stars">*****</div>
                        <p className="academy-testimonial-quote">{item.quote}</p>
                        <hr className="academy-testimonial-divider" />
                        <div>
                            <div className="academy-testimonial-role">{item.role}</div>
                            <div className="academy-testimonial-org">{item.org}</div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    </section>
)

const TrainerSection = () => (
    <section className="academy-section academy-trainer">
        <div className="academy-section-inner">
            <div className="academy-trainer-wrap">
                <div className="academy-trainer-avatar">
                    <img src="/img/sarah_academy.jpeg" alt="Sarah Yam Chinese digital marketing specialist" />
                </div>
                <div>
                    <div className="academy-trainer-tag">Your Trainer</div>
                    <h2 className="academy-trainer-name">Sarah Yam</h2>
                    <p className="academy-trainer-title">Chinese Digital Marketing Specialist | MA E-Marketing & Social Media</p>
                    <p className="academy-trainer-bio">
                        Few Western specialists have Sarah&apos;s depth of practical China experience. Over 15 years, she has guided marketing teams at Fortune 500 and FTSE 100 companies through a market that most agencies fundamentally misread. Her clients leave with a clear picture of where to focus and the confidence to act on it.
                    </p>
                    <p className="academy-trainer-bio">
                        She holds an MA in E-Marketing and Social Media and has trained professionals across Europe, the US and Asia. Her standard for every session is straightforward: you should be able to act on what you have learned by the following morning.
                    </p>
                    <div className="academy-trainer-expertise-title">Training Expertise</div>
                    <ul className="academy-expertise-list">
                        <li>Chinese digital marketing and social media strategy</li>
                        <li>Using Chinese AI tools to get 10x more from your marketing time</li>
                        <li>Chinese e-commerce and social commerce</li>
                        <li>KOL and influencer marketing in China</li>
                        <li>Chinese content marketing and advertising strategy</li>
                    </ul>
                    <div className="academy-credentials">
                        <span className="academy-credential">MA E-Marketing & Social Media</span>
                        <span className="academy-credential">15+ Years China Experience</span>
                        <span className="academy-credential">Fortune 500 & FTSE 100 Clients</span>
                        <span className="academy-credential">B2C & B2B Specialist</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
)

class FormSection extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            messageSent: false
        }
        this.contactSchema = Yup.object().shape({
            name: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required').matches(/[^0-9]/, 'Numbers in the name?'),
            email: Yup.string().email('invalid email').required('Required!'),
            companyName: Yup.string().min(2, 'Too short!').required('Required!'),
            phoneNumber: Yup.number().required('Required').positive()
        })
    }

    render() {
        return (
            <section id="form" className="section request-form has-background-light">
                <div className="container">
                    <div className="columns">
                        <div className="column is-6">
                            <div className="academy-form-left">
                                <div className="academy-section-tag">Enquire</div>
                                <h2>
                                    Find the Right<br />
                                    <em>Programme for Your Team</em>
                                </h2>
                                <p>
                                    Tell us what you are working on and we will respond with an honest recommendation. Public programmes, private team workshops or a fully tailored engagement - we will help you find what fits.
                                </p>
                                <ul className="academy-form-reassure">
                                    <li>
                                        <div className="icon">★</div>
                                        <div><strong>5-star rated</strong> by marketing professionals from Fortune 500, FTSE 100 and leading global organisations. Every session is kept intentionally small to maintain quality.</div>
                                    </li>
                                    <li>
                                        <div className="icon">✓</div>
                                        <div><strong>Fully bespoke training</strong>, designed around your organisation, industry, and strategic objectives.</div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="column is-6">
                            <Formik
                                initialValues={{
                                    to: 'sarahyam%40reddigitalchina.com',
                                    cc: 'sunqqxp%40gmail.com',
                                    title: 'Training Brouchure Request',
                                    name: '',
                                    email: '',
                                    companyName: '',
                                    phoneNumber: '',
                                }}
                                validationSchema={this.contactSchema}
                                onSubmit={(values, { resetForm }) => {
                                    axios({
                                        method: 'post',
                                        url: 'https://8dbp32h7ge.execute-api.eu-west-1.amazonaws.com/dev/?format=json',
                                        data: `_to=${values['to']}&_cc=${values['cc']}&_replyTo=${values['email']}&title=${values['title']}&name=${values['name']}&email=${values['email']}&company=${values['companyName']}&phone=${values['phoneNumber']}`,
                                    }).then((response) => {
                                        if (response.data.statusCode == 200) {
                                            this.setState(prevState => ({
                                                messageSent: !prevState.messageSent
                                            }))
                                        }
                                        resetForm({
                                            to: 'sarahyam%40reddigitalchina.com',
                                            cc: 'sunqqxp%40gmail.com',
                                            title: 'Training Brouchure Request',
                                            name: '',
                                            email: '',
                                            companyName: '',
                                            phoneNumber: '',
                                        })
                                    }).catch((error) => {
                                        console.log(error)
                                    })
                                }}
                            >
                                {({ errors, touched }) => (
                                    <div className="academy-form-card">
                                        <h3 className="academy-form-card-title">Make an Enquiry</h3>
                                        <p className="academy-form-card-subtitle">Sarah personally reviews every enquiry and responds within one working day.</p>
                                        <div className="academy-form-inner">
                                            <Form id="Red Digital China Training brouchure Form">
                                                <div className="field">
                                                    <div className="field-body">
                                                        <div className="field">
                                                            <div className="control has-text-centered">
                                                                {this.state.messageSent ? (<p className="academy-form-status">Thank you for requesting training brochure from Red Digital Academy. Our training advisor will contact you by email or phone within 1-2 working days.</p>) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="field">
                                                    <label className="label is-medium">
                                                        Name*
                                                    </label>
                                                    <p className="control is-expanded has-icons-left">
                                                        <Field className="input is-medium" name="name" />
                                                        <span className="icon is-small is-left">
                                                            <i className="fas fa-user"></i>
                                                        </span>
                                                    </p>
                                                    {errors.name && touched.name ? (<p className="help is-danger">{errors.name}</p>) : null}
                                                </div>
                                                <div className="field">
                                                    <label className="label is-medium">
                                                        Email*
                                                    </label>
                                                    <p className="control is-expanded has-icons-left">
                                                        <Field className="input is-medium" name="email" type="email" />
                                                        <span className="icon is-small is-left">
                                                            <i className="fas fa-envelope"></i>
                                                        </span>
                                                    </p>
                                                    {errors.email && touched.email ? (<p className="help is-danger">{errors.email}</p>) : null}
                                                </div>
                                                <div className="field">
                                                    <label className="label is-medium">
                                                        Company*
                                                    </label>
                                                    <p className="control is-expanded has-icons-left">
                                                        <Field className="input is-medium" name="companyName" />
                                                        <span className="icon is-small is-left">
                                                            <i className="fas fa-building"></i>
                                                        </span>
                                                    </p>
                                                    {errors.companyName && touched.companyName ? (<p className="help is-danger">{errors.companyName}</p>) : null}
                                                </div>
                                                <div className="field">
                                                    <label className="label is-medium">
                                                        Phone*
                                                    </label>
                                                    <p className="control is-expanded has-icons-left">
                                                        <Field className="input is-medium" name="phoneNumber" />
                                                        <span className="icon is-small is-left">
                                                            <i className="fas fa-phone-volume"></i>
                                                        </span>
                                                    </p>
                                                    {errors.phoneNumber && touched.phoneNumber ? (<p className="help is-danger">Wrong phone number format</p>) : null}
                                                </div>
                                                <div className="field">
                                                    <div className="control has-text-centered">
                                                        <button className="button is-danger is-medium" type="submit">Send Enquiry</button>
                                                    </div>
                                                </div>
                                            </Form>
                                        </div>
                                    </div>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </section>
        )
    }
}

export default AcademyPage

export const pageQuery = graphql`
    query AcademyPage {
        site {
            siteMetadata {
                academyPage {
                    title
                    description
                    imageUrl
                    siteUrl
                }
            }
        }
    }
`
